import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "../../lib/utils";
import { Button } from "./button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover";
import { ScrollArea } from "./scroll-area";
import { truncateText, shouldShowTooltip } from "./text";

export interface ComboboxOption {
  value: string
  label: string
}

interface ComboboxProps {
  options: ComboboxOption[]
  value?: string
  onValueChange?: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  className?: string
  disabled?: boolean
}

export function Combobox({
  options,
  value,
  onValueChange,
  placeholder = "Select option...",
  searchPlaceholder = "Search...",
  emptyMessage = "No options found.",
  className,
  disabled = false,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);

  // Sort options alphabetically
  const sortedOptions = React.useMemo(() => {
    return [...options].sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
  }, [options]);

  const selectedOption = sortedOptions.find((option) => option.value === value);



  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("justify-between", className)}
          disabled={disabled}
          title={selectedOption && shouldShowTooltip(selectedOption.label) ? selectedOption.label : undefined}
        >
          <span className="truncate">
            {selectedOption ? truncateText(selectedOption.label) : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="p-0" 
        style={{ zIndex: 2147483647 }}
        onWheel={(e) => {
          e.stopPropagation();
          const isScrollingDown = e.deltaY > 0;
          const event = new KeyboardEvent("keydown", {
            key: isScrollingDown ? "ArrowDown" : "ArrowUp",
            bubbles: true,
          });
          e.target.dispatchEvent(event);
        }}
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <ScrollArea className="h-[200px]">
              <CommandGroup>
                {sortedOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={`${option.value}\u0001${option.label}`}
                    onSelect={(currentValue) => {
                      // Extract the actual value using a control character (U+0001) as delimiter (unlikely to appear in user input)
                      const [optionValue] = currentValue.split("\u0001");
                      const newValue = optionValue === value ? "" : optionValue;
                      onValueChange?.(newValue);
                      setOpen(false);
                    }}
                    title={shouldShowTooltip(option.label) ? option.label : undefined}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === option.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="truncate">{truncateText(option.label)}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
} 